/* eslint-disable jsdoc/require-returns */
/* eslint-disable jsdoc/require-param */
import { FF_ENABLE_ESCRIPT_ORDERS } from '@/constants/featureFlags';
import {
  ESCRIPT_ORDERS_ROUTE,
  ESCRIPT_PRODUCT_DETAIL_ROUTE,
  ESCRIPT_REFILL_PHARMACY_ROUTE,
  ESCRIPT_REFILL_ROUTE,
  FAQ_ROUTE,
  PATIENT_ORDERS_ROUTE,
  PATIENT_PRODUCT_DETAIL_ROUTE,
  PATIENT_REFILL_PHARMACY_ROUTE,
  PATIENT_REFILL_ROUTE,
  PRESCRIPTION_ROUTE,
  PRESCRIPTIONS_ROUTE,
  PROFILE_DETAILS_ROUTE,
} from '@/routes/route-paths';
import { Logger } from '@/utils/logger';
import { getUrlWithoutOrigin, replacePath, useMagicLinkAuthNavigation, useSession } from '@montugroup/circuit-shared';
import { getTsApiV1Client } from '@montugroup/circuit-shared/api/api-v1.client';
import { viteDomainSplitBaseUrl } from '@montugroup/circuit-shared/constants/env';
import { useLayoutEffect } from 'react';
import { matchPath, useLocation, useNavigate, useParams } from 'react-router-dom';
import useFeatureFlags from './useFeatureFlags';
import useCustomer from './user/useCustomer';

const logger = new Logger('useCustomerTypeRedirector');

/**
 * If these routes are hit by an patient customer (!isExternal) they are redirected from the key's path to the value's path (within customer-portal)
 */
export const PATIENT_ORDER_ROUTE_REDIRECTS: Record<string, string> = {
  [ESCRIPT_REFILL_ROUTE]: PATIENT_REFILL_ROUTE,
  [ESCRIPT_REFILL_PHARMACY_ROUTE]: PATIENT_REFILL_PHARMACY_ROUTE,
  [ESCRIPT_ORDERS_ROUTE]: PATIENT_ORDERS_ROUTE,
  [ESCRIPT_PRODUCT_DETAIL_ROUTE]: PATIENT_PRODUCT_DETAIL_ROUTE,
};

/**
 * If these routes are hit by an escript customer (isExternal) they are redirected from the key's path to the value's path (within customer-portal)
 *
 * These routes contain the PATIENT_ORDER_ROUTE_REDIRECTS routes but inverted
 */
export const ESCRIPT_ORDER_ROUTE_REDIRECTS: Record<string, string> = Object.fromEntries(
  Object.entries(PATIENT_ORDER_ROUTE_REDIRECTS).map(([key, value]) => [value, key]),
);

/**
 * If these routes are hit by an patient customer (!isExternal) they are redirected to the value's path
 * **but always on the patient-portal domain**
 */
export const PATIENT_MEDICAL_ROUTE_REDIRECTS: string[] = [
  PROFILE_DETAILS_ROUTE,
  FAQ_ROUTE,
  PRESCRIPTION_ROUTE,
  PRESCRIPTIONS_ROUTE,
];

/**
 * Handles redirections for routes originating in the customer-portal and is based on if the customer requires escript
 * ordering flow or existing Alt patient ordering flow
 */
export function useCustomerTypeRedirector() {
  const { session } = useSession();
  const { data: customer, isLoading: customerIsLoading } = useCustomer();
  const { flags } = useFeatureFlags();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { magicLinkAuthNavigation, isPending: isPendingRedirection } = useMagicLinkAuthNavigation();

  const isEscriptCustomer = customer?.isExternal && flags[FF_ENABLE_ESCRIPT_ORDERS];

  useLayoutEffect(() => {
    if (!customer || customerIsLoading || isPendingRedirection) {
      return;
    }

    let matchedPath;
    let patientPortalMatchedPath;

    if (isEscriptCustomer) {
      matchedPath = Object.keys(ESCRIPT_ORDER_ROUTE_REDIRECTS).find((path) => matchPath({ path }, location.pathname));
    } else {
      matchedPath = Object.keys(PATIENT_ORDER_ROUTE_REDIRECTS).find((path) => matchPath({ path }, location.pathname));
      patientPortalMatchedPath = PATIENT_MEDICAL_ROUTE_REDIRECTS.find((path) => matchPath({ path }, location.pathname));
    }

    if (matchedPath) {
      let newPath = ESCRIPT_ORDER_ROUTE_REDIRECTS[matchedPath] || PATIENT_ORDER_ROUTE_REDIRECTS[matchedPath];

      Object.entries(params).forEach(([key, value]) => {
        newPath = newPath?.replace(`:${key}`, value ?? '');
      });

      const currentURL = window.location.href;
      const urlWithNewPath = replacePath({ url: currentURL, newPath });
      const urlWithNewPathWithoutOrigin = getUrlWithoutOrigin(urlWithNewPath);

      logger.info('navigating', {
        isEscriptCustomer,
        matchedPath,
        oldPath: location.pathname,
        newPath,
        params,
        currentURL,
        urlWithNewPath,
        urlWithNewPathWithoutOrigin,
      });

      navigate(urlWithNewPathWithoutOrigin, { replace: true });
    } else if (patientPortalMatchedPath) {
      const currentURL = window.location.href;
      const newOrigin = viteDomainSplitBaseUrl('patient');

      logger.info('navigating::magicLinkAuthNavigation', {
        fromPortal: 'customer',
        toPortal: 'patient',
        isEscriptCustomer,
        matchedPath,
        oldPath: location.pathname,
        newPath: patientPortalMatchedPath,
        currentURL,
        newOrigin,
        params,
      });

      magicLinkAuthNavigation({
        currentURL,
        newOrigin,
        apiClient: getTsApiV1Client(),
        authorization: session?.token,
      });
    }
  }, [
    customer,
    customerIsLoading,
    isEscriptCustomer,
    isPendingRedirection,
    location.pathname,
    magicLinkAuthNavigation,
    navigate,
    params,
    session?.token,
  ]);

  return {
    isPendingRedirection,
  };
}
